import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BrandIcon } from "@/components/BrandIcon";
import { TurnstileSlot } from "@/components/TurnstileSlot";
import { IS_PRODUCTION, IS_STAGING } from "@/lib/env";
import { extractTenantSubdomain } from "@/lib/tenantFromHost";
import { startApi, type StartProvider } from "@/lib/startApi";
import { leaveFor } from "@/lib/startNavigation";
import {
  EMAIL_LABEL,
  LANDING_EMAIL_NOTE,
  LANDING_OPTIONAL,
  LOOKING,
  NO_MATCH,
  SEND_ME_A_CODE,
  UNAVAILABLE,
  codeRequested,
  landingIntro,
  matched,
  noticeFor,
  noticeText,
  type StartNotice,
} from "@/lib/startCopy";

/**
 * `/start`: an applicant who has just applied proves the email address they
 * applied with, by a sign-in or a one-time code, before any session exists.
 * Then the page asks once whether that address has an application here.
 *
 * Nothing on this page is logged, stored or sent anywhere but the start API:
 * not the address, the code, the proof id, or the link the applicant is sent
 * on to. The proof id lives in component state only, so a reload means a new
 * code. The route is on Clarity's blocked list (`clarity.ts`).
 */

const PROVIDER_NAMES: Record<StartProvider, string> = {
  google: "Google",
  linkedin: "LinkedIn",
  apple: "Apple",
  microsoft: "Microsoft",
};

type View =
  | { name: "landing" }
  | { name: "code"; stage: "email" | "entry" }
  | { name: "looking" }
  | { name: "no_match" }
  | { name: "matched"; employer: string | null; jobTitle: string | null };

type ProviderReturn =
  | { status: "proven"; proofId: string | null }
  | { status: "not_proven" | "cancelled" | "error" };

/** The org: the host's subdomain, or `?org=` where the host has none. */
function resolveOrg(): string | null {
  const fromHost = extractTenantSubdomain(window.location.hostname);
  if (fromHost) return fromHost;
  const fromQuery = new URLSearchParams(window.location.search).get("org")?.trim();
  return fromQuery || null;
}

function readJob(): string | null {
  return new URLSearchParams(window.location.search).get("job")?.trim() || null;
}

/** A sign-in coming back: `?status=` and, when proven, `#proof=`. */
function readProviderReturn(): ProviderReturn | null {
  const status = new URLSearchParams(window.location.search).get("status");
  if (status === "proven") {
    const proofId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("proof");
    return { status, proofId: proofId || null };
  }
  if (status === "not_proven" || status === "cancelled" || status === "error") return { status };
  return null;
}

/** Whether the address bar holds anything a sign-in's return left there. */
function hasReturnLeftovers(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("status") || params.has("provider") || window.location.hash !== "";
}

/**
 * Takes the sign-in's result out of the address bar: the fragment (which
 * holds the proof id), and `status` and `provider`, so a reload or a copied
 * link does not replay it. `org` and `job` stay.
 */
function clearProviderReturn() {
  const params = new URLSearchParams(window.location.search);
  params.delete("status");
  params.delete("provider");
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}`,
  );
}

/** Only a web address is followed, never another scheme; https only in production. */
function isWebUrl(url: unknown): url is string {
  if (typeof url !== "string") return false;
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || (!IS_PRODUCTION && protocol === "http:");
  } catch {
    return false;
  }
}

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

function Notice({ notice }: { notice: StartNotice }) {
  const { title, detail } = noticeText(notice);
  return (
    <div role="alert" className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
      {title && <p className="font-medium text-foreground">{title}</p>}
      <p className="text-muted-foreground">{detail}</p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-xl">
      <Card>
        <CardContent className="space-y-5 p-6">{children}</CardContent>
      </Card>
    </div>
  );
}

export default function StartPage() {
  const [org] = useState(resolveOrg);
  const [job] = useState(readJob);
  const [providerReturn] = useState(readProviderReturn);

  const [view, setView] = useState<View>(() =>
    providerReturn?.status === "not_proven" ? { name: "code", stage: "email" } : { name: "landing" },
  );
  const [notice, setNotice] = useState<StartNotice | null>(() => {
    if (!providerReturn) return null;
    if (providerReturn.status === "not_proven") return { kind: "not_proven" };
    if (providerReturn.status === "error") return { kind: "our_side" };
    if (providerReturn.status === "proven" && !providerReturn.proofId) return { kind: "our_side" };
    return null;
  });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [proofId, setProofId] = useState<string | null>(null);
  const [stagingCode, setStagingCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const context = useQuery({
    queryKey: ["start-context", org, job],
    queryFn: () => startApi.getContext(org as string, job),
    enabled: Boolean(org),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  /** Asks once whether the proven address has an application here. */
  const continueWithProof = async (proof: string) => {
    if (!org) return;
    setNotice(null);
    setView({ name: "looking" });
    try {
      const result = await startApi.continueWithProof(org, proof);
      if (result?.outcome === "matched" && isWebUrl(result.continue_url)) {
        setView({ name: "matched", employer: text(result.employer), jobTitle: text(result.job_title) });
        leaveFor(result.continue_url);
        return;
      }
      setView({ name: "no_match" });
    } catch (e) {
      setNotice(noticeFor(e));
      setView({ name: "landing" });
    }
  };

  // A sign-in's return is read once (the initial state above) and handled
  // once, even when effects run twice in development.
  const handledReturn = useRef(false);
  useEffect(() => {
    if (handledReturn.current) return;
    handledReturn.current = true;
    if (hasReturnLeftovers()) clearProviderReturn();
    if (providerReturn?.status === "proven" && providerReturn.proofId) {
      void continueWithProof(providerReturn.proofId);
    }
    // Runs once on mount by design; `continueWithProof` reads only stable values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from a provider's page, or from the applicant flow after a match,
  // can restore this page as it was left: busy, or on the matched screen,
  // whose link was followed once and is not kept. Start again from the
  // first screen.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      setBusy(false);
      setView((v) => (v.name === "matched" || v.name === "looking" ? { name: "landing" } : v));
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const signIn = async (provider: StartProvider) => {
    if (!org) return;
    setBusy(true);
    setNotice(null);
    try {
      const { redirect_url } = await startApi.startOAuth(org, provider, job);
      if (!isWebUrl(redirect_url)) throw new Error("Unexpected sign-in address");
      leaveFor(redirect_url);
    } catch (e) {
      setNotice(noticeFor(e));
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (!org) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await startApi.sendCode(org, email.trim(), job);
      setProofId(res.proof_id);
      const shown = IS_STAGING && res.code ? res.code : null;
      setStagingCode(shown);
      setCode(shown ?? "");
      setView({ name: "code", stage: "entry" });
    } catch (e) {
      setNotice(noticeFor(e));
    } finally {
      setBusy(false);
    }
  };

  /** Back to the address field, keeping the address: every "new code" recovery. */
  const backToSend = () => {
    setProofId(null);
    setCode("");
    setStagingCode(null);
    setView({ name: "code", stage: "email" });
  };

  const confirmCode = async () => {
    if (!org || !proofId || code.length < 6) return;
    setBusy(true);
    setNotice(null);
    let proven: string | null = null;
    try {
      const res = await startApi.confirmCode(org, proofId, code);
      // A wrong code is a 200 with `verified: false`. The code stays in the
      // field so the applicant can see and correct it.
      if (res?.verified) proven = proofId;
      else setNotice({ kind: "wrong_code" });
    } catch (e) {
      const next = noticeFor(e);
      setNotice(next);
      if (next.kind === "code_expired" || next.kind === "too_many_attempts") backToSend();
    } finally {
      setBusy(false);
    }
    if (proven) {
      setProofId(null);
      setCode("");
      await continueWithProof(proven);
    }
  };

  // ---------- After the proof ----------

  if (view.name === "looking") {
    return (
      <Shell>
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {LOOKING}
        </p>
      </Shell>
    );
  }
  if (view.name === "no_match") {
    return (
      <Shell>
        <p role="status" className="text-sm leading-relaxed text-foreground">{NO_MATCH}</p>
      </Shell>
    );
  }
  if (view.name === "matched") {
    return (
      <Shell>
        <p role="status" className="flex items-start gap-2 text-sm leading-relaxed text-foreground">
          <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" />
          <span>{matched(view.jobTitle, view.employer)}</span>
        </p>
      </Shell>
    );
  }

  // ---------- Before the proof ----------

  if (!org || (context.isError && noticeFor(context.error).kind === "unavailable")) {
    return (
      <Shell>
        <p className="text-sm leading-relaxed text-foreground">{UNAVAILABLE}</p>
      </Shell>
    );
  }
  if (context.isError) {
    return (
      <Shell>
        <Notice notice={noticeFor(context.error)} />
      </Shell>
    );
  }
  if (!context.data) {
    return (
      <Shell>
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="sr-only">Loading</span>
        </p>
      </Shell>
    );
  }

  const ctx = context.data;
  const employer = ctx.employer.name;
  const codeOpen = view.name === "code";
  const entering = view.name === "code" && view.stage === "entry";

  return (
    <Shell>
      <div className="space-y-3">
        <h1 className="text-xl font-semibold text-foreground">Trust Checkpoint</h1>
        <p className="text-sm leading-relaxed text-foreground">{landingIntro(employer, ctx.job?.title ?? null)}</p>
        <p className="text-sm font-medium text-foreground">{LANDING_OPTIONAL}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{LANDING_EMAIL_NOTE}</p>
      </div>

      {notice && <Notice notice={notice} />}

      {ctx.providers.length > 0 && (
        <div className="space-y-2">
          <TurnstileSlot config={ctx.turnstile} placement="sign-in" />
          {ctx.providers.map((p) => (
            <Button
              key={p}
              variant="outline"
              className="w-full justify-center gap-2"
              disabled={busy}
              onClick={() => void signIn(p)}
            >
              <BrandIcon brand={p} />
              Continue with {PROVIDER_NAMES[p] ?? p}
            </Button>
          ))}
        </div>
      )}

      {!codeOpen ? (
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setView({ name: "code", stage: "email" });
          }}
          disabled={busy}
          className="block w-full text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
        >
          {SEND_ME_A_CODE}
        </button>
      ) : (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-trust-blue" />
            <label htmlFor="start-email" className="text-sm font-medium text-foreground">
              {EMAIL_LABEL}
            </label>
          </div>
          <Input
            id="start-email"
            type="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            readOnly={entering}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!entering ? (
            <>
              <TurnstileSlot config={ctx.turnstile} placement="code" />
              <Button className="w-full" onClick={() => void sendCode()} disabled={busy || !email.includes("@")}>
                {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Send me a code
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-muted-foreground">{codeRequested(employer)}</p>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label="Code"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center font-mono tracking-[0.5em]"
              />
              {stagingCode && (
                <div className="rounded-md border border-amber-300/60 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-200">
                  Dev mode: your verification code is <span className="font-mono font-semibold">{stagingCode}</span>
                </div>
              )}
              <Button className="w-full" onClick={() => void confirmCode()} disabled={busy || code.length < 6}>
                {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Verify code
              </Button>
              <button
                type="button"
                onClick={() => void sendCode()}
                disabled={busy}
                className="block w-full text-center text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                Send a new code
              </button>
              <button
                type="button"
                onClick={() => {
                  setNotice(null);
                  backToSend();
                }}
                disabled={busy}
                className="block w-full text-center text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                Use a different email address
              </button>
            </>
          )}
        </div>
      )}
    </Shell>
  );
}
